import {
  AccommodationTypeValues,
  BeverageTypeValues,
  BuildingHeatingValues,
  CargoValues,
  Category,
  EnergyTypeValues,
  EquipmentTypeValues,
  FuelTypeValues,
  MealTypeValues,
  NonRecyclableWasteTypeValues,
  OfficeSuppliesNewValues,
  OfficeSuppliesRefurbishValues,
  RecyclableWasteTypeValues,
  ScenographieNoRecyclableValues,
  ScenographieRecyclableValues,
  TransportationModeValues,
} from '@/domain/impact-co2/entities/question.entity';

import { Question } from '../prepare-data-for-excel-export/preparaDataToForExcelExport';
import { getQuestionTranslate } from './translate';

type Fields = Question['fields'];

export function getFieldsByCategory(category: Category): Fields {
  const fields = fieldsByCategory[category];
  if (fields.length === 0) {
    throw new Error(`Fields for category ${category} not found`);
  }
  return fieldsByCategory[category];
}

// -------------------------------

class FieldBuilder2 {
  constructor(private readonly category: Category) {}
  buildField(key: string, type: string, values: string) {
    return {
      key,
      label: getQuestionTranslate(this.category, key),
      type,
      values,
    };
  }
}

function joinValues(values: string[]): string {
  return values.join(' | ');
}

// -------------------------------

const fieldsByCategory: Record<Category, Fields> = {
  ACCOMMODATION: buildAccommodationFields(),
  ACCOMMODATION_PUBLIC: buildAccommodationPublicFields(),
  BUILDING: buildBuildingFields(),
  CARGO: buildCargoFields(),
  CATERING_BEVERAGE: buildCateringBeverageFields(),
  CATERING_MEAL: buildCateringMealFields(),
  DIGITAL: buildDigitalFields(),
  ENERGY: buildEnergyFields(),
  EQUIPMENT: buildEquipmentFields(),
  GENERATOR: buildGeneratorFields(),
  MOBILITY: buildMobilityFields(),
  MOBILITY_PUBLIC: buildMobilityPublicFields(),
  NON_RECYCLABLE_WASTE: buildNonRecyclableWasteFields(),
  RECYCLABLE_WASTE: buildRecyclableWasteFields(),
  SCENOGRAPHY_NO_RECYCLABLE: buildScenographyNoRecyclableFields(),
  SCENOGRAPHY_RECYCLABLE: buildScenographyRecyclableFields(),
  BUILDING_HEATING: buildBuildingHeatingFields(),
  OFFICE_SUPPLIES_NEW: buildOfficeSuppliesNewFields(),
  OFFICE_SUPPLIES_REFURBISH: buildOfficeSuppliesRefurbishFields(),
  TEAM_MOBILITY: buildTeamMobilityFields(),
  WATER: buildWaterFields(),
};

// -------------------------------

// -------------------------------

function buildAccommodationFields(): Fields {
  const fb = new FieldBuilder2('ACCOMMODATION');
  return [
    fb.buildField(
      'accommodationType',
      'list',
      joinValues([...AccommodationTypeValues]),
    ),
    fb.buildField('numberOfNights', 'number', ''),
  ];
}

function buildAccommodationPublicFields(): Fields {
  const fb = new FieldBuilder2('ACCOMMODATION_PUBLIC');
  return [
    fb.buildField(
      'accommodationType',
      'list',
      joinValues([...AccommodationTypeValues]),
    ),
    fb.buildField('numberOfNights', 'number', ''),
    fb.buildField('guestCount', 'number', ''),
    fb.buildField('tags', 'list', ''),
  ];
}

function buildBuildingFields(): Fields {
  const fb = new FieldBuilder2('BUILDING');
  return [
    fb.buildField('hasBuildingImpact', 'boolean', ''),
    fb.buildField('buildingCarbonImpactPerDay', 'number', ''),
    fb.buildField('numberOfDaysOfUse', 'number', ''),
  ];
}

function buildCargoFields(): Fields {
  const fb = new FieldBuilder2('CARGO');
  return [
    fb.buildField('transportationMode', 'list', joinValues([...CargoValues])),
    fb.buildField('weight', 'number', ''),
    fb.buildField('travelDistance', 'number', ''),
  ];
}

function buildCateringBeverageFields(): Fields {
  const fb = new FieldBuilder2('CATERING_BEVERAGE');
  return [
    fb.buildField('beverageType', 'list', joinValues([...BeverageTypeValues])),
    fb.buildField('beverageCount', 'number', ''),
  ];
}

function buildCateringMealFields(): Fields {
  const fb = new FieldBuilder2('CATERING_MEAL');
  return [
    fb.buildField('mealType', 'list', joinValues([...MealTypeValues])),
    fb.buildField('mealCount', 'number', ''),
  ];
}

function buildDigitalFields(): Fields {
  const fb = new FieldBuilder2('DIGITAL');
  return [
    fb.buildField('dataUsage', 'number', ''),
    fb.buildField('videoViews', 'number', ''),
  ];
}

function buildEnergyFields(): Fields {
  const fb = new FieldBuilder2('ENERGY');
  return [
    fb.buildField('energyType', 'list', joinValues([...EnergyTypeValues])),
    fb.buildField('energyConsumptionQuantity', 'number', ''),
  ];
}

function buildEquipmentFields(): Fields {
  const fb = new FieldBuilder2('EQUIPMENT');
  return [
    fb.buildField(
      'rentedEquipmentType',
      'list',
      joinValues([...EquipmentTypeValues]),
    ),
    fb.buildField('rentalDays', 'number', ''),
    fb.buildField('quantity', 'number', ''),
  ];
}

function buildGeneratorFields(): Fields {
  const fb = new FieldBuilder2('GENERATOR');
  return [
    fb.buildField('hasGenerator', 'boolean', ''),
    fb.buildField('fuelType', 'list', joinValues([...FuelTypeValues])),
    fb.buildField('fuelConsumption', 'number', ''),
  ];
}

function buildMobilityFields(): Fields {
  const fieldBuilder = new FieldBuilder2('MOBILITY');
  return [
    fieldBuilder.buildField(
      'transportationMode',
      'list',
      joinValues([...TransportationModeValues]),
    ),
    fieldBuilder.buildField('passengerCount', 'number', ''),
    fieldBuilder.buildField('travelDistance', 'number', ''),
  ];
}

function buildMobilityPublicFields(): Fields {
  const fb = new FieldBuilder2('MOBILITY_PUBLIC');
  return [
    fb.buildField(
      'transportationMode',
      'list',
      joinValues([...TransportationModeValues]),
    ),
    fb.buildField('passengerCount', 'number', ''),
    fb.buildField('travelDistance', 'number', ''),
    fb.buildField('departurePoint', 'text', ''),
    fb.buildField('tags', 'list', ''),
  ];
}

function buildNonRecyclableWasteFields(): Fields {
  const fb = new FieldBuilder2('NON_RECYCLABLE_WASTE');
  return [
    fb.buildField(
      'nonRecyclableWasteType',
      'list',
      joinValues([...NonRecyclableWasteTypeValues]),
    ),
    fb.buildField('nonRecyclableWasteQuantity', 'number', ''),
  ];
}

function buildRecyclableWasteFields(): Fields {
  const fb = new FieldBuilder2('RECYCLABLE_WASTE');
  return [
    fb.buildField(
      'recyclableWasteType',
      'list',
      joinValues([...RecyclableWasteTypeValues]),
    ),
    fb.buildField('recyclableWasteQuantity', 'number', ''),
  ];
}

function buildScenographyNoRecyclableFields(): Fields {
  const fb = new FieldBuilder2('SCENOGRAPHY_NO_RECYCLABLE');
  return [
    fb.buildField(
      'scenographieNoRecyclableType',
      'list',
      joinValues([...ScenographieNoRecyclableValues]),
    ),
    fb.buildField('quantity', 'number', ''),
  ];
}

function buildScenographyRecyclableFields(): Fields {
  const fb = new FieldBuilder2('SCENOGRAPHY_RECYCLABLE');
  return [
    fb.buildField(
      'scenographieRecyclableType',
      'list',
      joinValues([...ScenographieRecyclableValues]),
    ),
    fb.buildField('quantity', 'number', ''),
  ];
}

function buildBuildingHeatingFields(): Fields {
  const fb = new FieldBuilder2('BUILDING_HEATING');
  return [
    fb.buildField(
      'buildingHeatingType',
      'list',
      joinValues([...BuildingHeatingValues]),
    ),
    fb.buildField('buildingSize', 'number', ''),
    fb.buildField('numberOfMonthsOfUse', 'number', ''),
  ];
}

function buildOfficeSuppliesNewFields(): Fields {
  const fb = new FieldBuilder2('OFFICE_SUPPLIES_NEW');
  return [
    fb.buildField(
      'officeSuppliesNewType',
      'list',
      joinValues([...OfficeSuppliesNewValues]),
    ),
    fb.buildField('count', 'number', ''),
  ];
}

function buildOfficeSuppliesRefurbishFields(): Fields {
  const fb = new FieldBuilder2('OFFICE_SUPPLIES_REFURBISH');
  return [
    fb.buildField(
      'officeSuppliesRefurbishType',
      'list',
      joinValues([...OfficeSuppliesRefurbishValues]),
    ),
    fb.buildField('count', 'number', ''),
  ];
}

function buildTeamMobilityFields(): Fields {
  const fb = new FieldBuilder2('TEAM_MOBILITY');
  return [
    fb.buildField(
      'transportationMode',
      'list',
      joinValues([...TransportationModeValues]),
    ),
    fb.buildField('passengerCount', 'number', ''),
    fb.buildField('travelDistance', 'number', ''),
    fb.buildField('numberOfDays', 'number', ''),
  ];
}

function buildWaterFields(): Fields {
  const fb = new FieldBuilder2('WATER');
  return [fb.buildField('waterConsumptionVolume', 'number', '')];
}

// -------------------------------
